import React, { useMemo, useState, useEffect } from 'react';
import {
	Typography,
	TableContainer,
	Table,
	TableBody,
	TableRow,
	TableCell,
	Paper,
	TablePagination,
	Box,
	Skeleton,
	useTheme,
} from '@mui/material';
import { FolioTableHead } from '@/components/table/FolioTableHead';
import { TableProgressIndicator } from '@/components/table/TableProgressIndicator';
import { invoiceHeadCells, policyHeadCells, policyTransactionHeadCells } from '@/utils/constants';
import { stableSort } from '@/components/table/functions';
import { useSearchInsightPolicyTransactionsInfiniteQuery } from '@/features/policies/policiesApi';
import { FILTER_TYPES } from '@/features/policies/data';
import { useSelector } from 'react-redux';
import { useGetBrokerGroupDetailsQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { InvoiceTransactionRow } from '../InvoiceTransactionRow';

export const SelectPolicyTransactionTable = ({
	clientId,
	transactions = [],
	setSelectedTransactions,
	showDisabledRows = false,
	policyId,
	type,
	displayAllTransactions = false,
}) => {
	const theme = useTheme();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const headerCellMap = {
		transaction: policyTransactionHeadCells,
		invoice: invoiceHeadCells,
		policy: policyHeadCells,
	};

	const headerCells = headerCellMap[type] || [];
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { data: brokerGroup, ...brokerGroupQuery } = useGetBrokerGroupDetailsQuery(
		{ docId: userDetails?.brokerGroupId },
		{ skip: !userDetails?.brokerGroupId },
	);

	useEffect(() => {
		setPage(0);
	}, [displayAllTransactions, clientId]);

	//If type is transaction don't pass the clientId, passing the policyId would filter only transactions assocaited (clientId overrides)
	const { data, isLoading, isError, isFetching, refetch } =
		useSearchInsightPolicyTransactionsInfiniteQuery(
			{
				...(!userDetails?.canViewGroup && { authRepId: userDetails?.authorisedRepId }),
				...(policyId && { policyId }),
				...(!policyId && clientId && { clientId }),
				filterBy: displayAllTransactions
					? FILTER_TYPES.ShowAll
					: type === 'invoice'
						? FILTER_TYPES.ShowOutstanding
						: FILTER_TYPES.ShowLatest,
			},
			{
				skip:
					!userDetails ||
					!(clientId || policyId) ||
					(type !== 'policy' && !userDetails?.branchId && !brokerGroup?.branchId),
			},
		);

	useEffect(() => {
		if (!clientId && !policyId) return;

		if (!userDetails) return;

		if (userQuery.isFetching || brokerGroupQuery.isFetching) return;

		refetch();
	}, [
		clientId,
		policyId,
		userDetails,
		userQuery.isFetching,
		brokerGroupQuery.isFetching,
		displayAllTransactions,
	]);

	const policyTransRows = useMemo(() => {
		const rows = data?.pages.flatMap((p) => p.Results ?? []) ?? [];
		return rows.map((r) => ({
			...r,
			clientDocId: r.ClientDocId,
			ownerDocId: r.OwnerDocId
		}));
	}, [data]);

	const loading = isLoading || userQuery.isFetching || brokerGroupQuery.isFetching || isFetching;
	const error = isError;

	const totalCount = useMemo(() => {
		if (data?.pages?.[0]?.TotalCount) {
			return data.pages[0].TotalCount;
		}

		return policyTransRows.length;
	}, [data, policyTransRows.length]);

	const handleCheckboxChange = (event, policyTransId) => {
		const policy = policyTransRows.find((p) => p.Id === policyTransId);
		if (!policy) return;
		const isCurrentlySelected = transactions.some(
			(t) => (t.policyTransId ?? t.Id) === policyTransId,
		);

		let newTransactions;
		if (isCurrentlySelected) {
			newTransactions = transactions.filter(
				(t) => (t.policyTransId ?? t.Id) !== policyTransId,
			);
		} else {
			const normalizedPolicy = {
				...policy,
				policyTransId: policy.Id,
			};
			newTransactions = [...transactions, normalizedPolicy];
		}

		setSelectedTransactions(newTransactions);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		const newRowsPerPage = parseInt(event.target.value, 10);
		setRowsPerPage(newRowsPerPage);
		setPage(0);
	};

	const currentPageRows = useMemo(() => {
		if (!displayAllTransactions || policyTransRows.length === 0) {
			return stableSort(policyTransRows, (a, b) => b.Id - a.Id);
		}
		const sortedRows = stableSort(policyTransRows, (a, b) => b.Id - a.Id);
		const startIndex = page * rowsPerPage;
		const endIndex = startIndex + rowsPerPage;
		return sortedRows.slice(startIndex, endIndex);
	}, [policyTransRows, displayAllTransactions, page, rowsPerPage]);

	return (
		<TableContainer
			component={Paper}
			sx={{
				borderRadius: '8px',
				overflow: 'auto',
				boxShadow: 1,
				mt: 1,
				border: error ? '1px solid red' : 'none',
				backgroundColor: theme.palette.background.paper,
			}}
		>
			<Table
				aria-labelledby='tableTitle'
				size='medium'
				aria-label='enhanced table'
				sx={{
					minWidth: 700,
					'& .MuiTableRow-root:last-child .MuiTableCell-root': {
						borderBottom: 'none',
					},
				}}
			>
				<FolioTableHead
					order='desc'
					orderBy='createDate'
					headCells={headerCells}
					headerBackground={true}
				/>
				<TableBody>
					{loading ? (
						<TableProgressIndicator rowCount={headerCells.length} />
					) : error ? (
						<TableRow>
							<TableCell colSpan={headerCells.length} align='center'>
								<Typography variant='subtitle1'>
									There was an error. Please try again.
								</Typography>
							</TableCell>
						</TableRow>
					) : currentPageRows.length > 0 ? (
						currentPageRows.map((transaction) => (
							<InvoiceTransactionRow
								showDisabledRow={false}
								hideCheckbox={false}
								key={`policy-trans-${transaction.Id}`}
								transaction={transaction}
								selected={transactions.some(
									(t) => (t.policyTransId ?? t.Id) === transaction.Id,
								)}
								isInvoiceRow={type === 'invoice'}
								handleCheckboxChange={handleCheckboxChange}
							/>
						))
					) : (
						<TableRow>
							<TableCell colSpan={headerCells.length} align='center'>
								<Typography variant='subtitle1'>
									No policy transactions found
								</Typography>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
			{displayAllTransactions && !loading && (
				<TablePagination
					rowsPerPageOptions={[5, 10, 25, 50]}
					component='div'
					count={totalCount}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					showFirstButton={true}
					showLastButton={true}
					sx={{
						borderTop: `1px solid ${theme.palette.divider}`,
						backgroundColor: theme.palette.background.paper,
						'& .MuiTablePagination-select': {
							backgroundColor: theme.palette.background.paper,
						},
						'& .MuiTablePagination-actions': {
							backgroundColor: theme.palette.background.paper,
						},
					}}
				/>
			)}
		</TableContainer>
	);
};
